import React, { Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import { isString } from 'lodash';
import ModalModule from '../modal';
import Utils from '../utils';
import UI from '../ui';
import Communication from '../utils/Communication';
import AutoFundAccountModal from './AutoFundAccountModal.component';
import ConfirmTransactionModal from './ConfirmTransactionModal.component';
import ProcessingPurchaseModal from './ProcessingPurchaseModal.component';
import AccountBalanceLimitModal from './AccountBalanceLimitModal.component';
import Link from '../ui/Link.component';
import Chatbot from '../chatbot';

const { Modal, ModalButtonActionType } = ModalModule;
const { withLayoutAndModalContexts, Navigation, Session, Analytics } = Utils;
const {
	WrappedButton,
	WrappedButtonType,
	WrappedRadioButton,
	WrappedInputNumberWithLabel,
	InlineMessage,
} = UI;

const { ChatbotInitializer, ChatbotTriggers, ChatbotMessengerState } = Chatbot;

// Use JavaScript Intl.NumberFormat to format fund amount value
const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

class AddFundsModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			customAmount: null,
			selectedOption: '',
			purchaseAmount: null,
			showInlineErrorMessage: false,
		};
	}

	/**
	 * Call purchasePostage endpoint
	 */
	purchasePostage = async () => {
		const {
			data,
			modalId,
			modalContext: { closeModal, showError, showMessage, showModal },
		} = this.props;
		const { onPurchaseSuccessful } = data || {};
		const { purchaseAmount } = this.state;
		const { lifetimeFundingLimit } = Session.getAccountInfo();

		// show the `ProcessingPurchaseModal` dialog
		const processingPurchaseModalId = showModal({
			component: ProcessingPurchaseModal,
			closeCallback: () => {
				// close "AddFundsModal" component after closing the `ProcessingPurchaseModal` modal
				closeModal(modalId, ModalButtonActionType.Ok);
			},
		});

		const response = await Communication.purchasePostage({
			amount: purchaseAmount,
		});

		if (Communication.isSuccessful(response)) {
			// calls the callback passed by the caller
			if (onPurchaseSuccessful) {
				onPurchaseSuccessful();
			}

			Analytics.trackEvent(
				'addFunds',
				{
					amount: purchaseAmount,
					success: true,
				},
				Analytics.agent.segment
			);

			if (lifetimeFundingLimit) {
				const balance = Session.getBalance();
				const { controlTotal = 0 } = balance;
				const remainingBalance = lifetimeFundingLimit - controlTotal;
				const status = remainingBalance > 0 ? 'approaching' : 'reached';

				// Track the funding limit status after successful purchase
				Analytics.trackEvent(
					'funding-limit-purchase',
					{
						limit_remaining: remainingBalance,
						purchase_amount: purchaseAmount,
						funding_status: status,
						purchase_success: true
					},
					Analytics.agent.segment
				);
			}

			const description = ReactDOMServer.renderToStaticMarkup(
				<div>
					<p>
						Your fund request for {usCurrencyFormatter.format(purchaseAmount)} has been
						approved.
					</p>
					{purchaseAmount === lifetimeFundingLimit &&
						this.getInfoBanner('warning', lifetimeFundingLimit)}
				</div>
			);

			showMessage({
				data: {
					title: 'Transaction Complete',
					description,
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'OK',
				},
			});
		} else {
			Analytics.trackEvent(
				'addFunds',
				{
					amount: purchaseAmount,
					success: false,
					error: response,
				},
				Analytics.agent.segment
			);

			if (lifetimeFundingLimit) {
				const balance = Session.getBalance();
				const { controlTotal = 0 } = balance;
				const remainingBalance = lifetimeFundingLimit - controlTotal;
				const status = remainingBalance > 0 ? 'approaching' : 'reached';

				// Track the funding limit status after unsuccessful purchase
				Analytics.trackEvent(
					'funding-limit-purchase',
					{
						limit_remaining: remainingBalance,
						purchase_amount: purchaseAmount,
						funding_status: status,
						purchase_success: false
					},
					Analytics.agent.segment
				);
			}

			const error = {
				errorCode: response?.error?.errorCode,
				errorMessage: isString(response?.error?.errorMessage)
					? response.error.errorMessage
					: response.ErrorDescription,
			};

			if (error.errorCode === 6226727) {
				const { brandName } = Session.getBrand();
				const brandPhone = brandName === 'Stamps.com' ? '888-434-0055' : '800-576-3279';
				showError({
					data: {
						title: 'Payment Processing Error',
						description: `
							<p>We’ve encountered an error processing your payment. Please verify your payment information is correct and try again.</p>
							<p>If you continue to receive this message, please contact our support team for assistance at ${brandPhone} Monday–Friday 8am - 8pm CT.</p>
							`,
						primaryButtonText: 'OK',
						handleType: 'custom',
					},
				});
			} else {
				showError({ response });
			}
		}

		// close `ProcessingPurchaseModal` when the response comes back
		closeModal(processingPurchaseModalId);
	};

	/**
	 * Change selected add-fund option
	 * @param {object} event
	 */
	onRadioOptionChange = ({ target }) => {
		const { customAmount } = this.state;
		const { name, value } = target;

		this.setState({
			purchaseAmount: name === 'customAmount' ? customAmount : value,
			selectedOption: name,
			showInlineErrorMessage: false,
		});
	};

	/**
	 * Change the custom amount and selected add-fund option to `customAmount`
	 * @param {object} event
	 */
	onCustomAmountChange = ({ target }) => {
		const { name, value } = target;

		this.setState({
			selectedOption: name,
			purchaseAmount: value,
			customAmount: value,
			showInlineErrorMessage: false,
		});
	};

	/**
	 * Show AutoFundAccountModal modal
	 */
	onAutoBuyPostageLinkClick = () => {
		const {
			modalContext: { showModal },
		} = this.props;

		showModal({
			component: AutoFundAccountModal,
		});
	};

	/**
	 * Navigate to editPaymentMethod
	 */
	onEditPaymentMethodLinkClick = () => {
		Navigation.navigateTo('editPaymentMethod');
	};

	/**
	 * Change `selectedOption` and `purchaseAmount` to `customAmount`
	 */
	onFocus = () => {
		const { customAmount } = this.state;

		this.setState({
			selectedOption: 'customAmount',
			purchaseAmount: customAmount,
			showInlineErrorMessage: false,
		});
	};

	/**
	 * Navigate to earnCredit
	 */
	onReferFriendLinkClick = () => {
		Navigation.navigateTo('earnCredit');
	};

	/**
	 * Validate `purchaseAmount` value before showing `ConfirmTransactionModal`
	 */
	onPurchaseClick = async () => {
		const {
			modalContext: { showModal, showError },
		} = this.props;
		const { purchaseAmount } = this.state;
		const balance = Session.getBalance();
		const { controlTotal = 0 } = balance;
		const { lifetimeFundingLimit } = Session.getAccountInfo();
		const remainingBalance = lifetimeFundingLimit - controlTotal;

		const errorMessage = {
			title: 'Notice',
			primaryButtonText: 'OK',
			handleType: 'custom',
		};

		if (lifetimeFundingLimit && purchaseAmount && purchaseAmount > lifetimeFundingLimit - controlTotal) {
			this.setState({
				showInlineErrorMessage: true,
			});

			// Track that user attempted to purchase postage after reaching their funding limit
			Analytics.trackEvent(
				'funding-limit-purchase',
				{
					limit_remaining: remainingBalance,
					purchase_amount: purchaseAmount,
					funding_status: 'blocked',
					purchase_success: null
				},
				Analytics.agent.segment
			);
		} else {
			if (!purchaseAmount || purchaseAmount === 0) {
				return showError({
					data: {
						...errorMessage,
						description: '<p>Please select an amount to add.</p>',
					},
				});
			} else if (purchaseAmount < 10) {
				return showError({
					data: {
						...errorMessage,
						description: '<p>You must add at least $10.</p>',
					},
				});
			} else if (purchaseAmount + balance.amountAvailable > balance.maxBalanceAmountAllowed) {
				return showModal({
					component: AccountBalanceLimitModal,
					data: {
						maxPostageBalance: usCurrencyFormatter.format(balance.maxBalanceAmountAllowed),
					},
				});
			}

			showModal({
				component: ConfirmTransactionModal,
				data: {
					purchaseAmount: usCurrencyFormatter.format(purchaseAmount),
					purchasePostageCallback: this.purchasePostage,
				},
				closeCallback: type => {
					/**
					 * When click the "Confirm" button, continue the purchase flow
					 * "Confirm" type is used to differentiate from "Esc" or "X" (which are "OK" type)
					 */
					if (type === ModalButtonActionType.Confirm) {
						this.purchasePostage();
					}
				},
			});
		}
	};

	getInfoBanner = (infoBannerType, lifetimeFundingLimit) => {
		const { brandName } = Session.getBrand();
		const isEndicia = brandName === 'Endicia';
		const balance = Session.getBalance();
		const { controlTotal = 0 } = balance;
		const remainingBalance = lifetimeFundingLimit - controlTotal;

		if (!isEndicia) {
			return (
				<InlineMessage
					type={infoBannerType}
					header={
						infoBannerType === 'warning'
							? 'You’ve reached your account funding limit.'
							: `For your protection, new accounts have a $${lifetimeFundingLimit} funding limit.`
					}
					text={
						<>
							To remove this limit, contact our support team at 1 (844) 522-7314 or
							chat with a live agent (available Monday–Friday 8am - 8pm CT).
							{infoBannerType === 'error' &&
								' You can then add more funds to your account.'}
						</>
					}
					actions={
						<WrappedButton
							label="Start chat"
							buttonType={WrappedButtonType.TertiaryWhite}
							dataqa="start-chat"
							onClick={() => {
								// Close the modal first
								const {
									modalId,
									modalContext: { closeModal },
								} = this.props;
								closeModal(modalId, ModalButtonActionType.Ok);

								// Track that user opened chat from limited funding message
								Analytics.trackEvent(
									'live-chat-transfer',
									{
										limit_remaining: remainingBalance,
									},
									Analytics.agent.segment
								);

								// Then start the chat
								ChatbotInitializer.start({
									chatbotState: ChatbotMessengerState.OPEN,
									logTrackEvent: true,
									trigger: ChatbotTriggers.AddFunds,
								});
							}}
						/>
					}
					dataqa="warning-message"
				/>
			);
		} else {
			return (
				<InlineMessage
					type={infoBannerType}
					header={
						infoBannerType === 'warning'
							? 'You’ve reached your account funding limit.'
							: `For your protection, new accounts have a $${lifetimeFundingLimit} funding limit.`
					}
					text={
						<>
							Contact our support team at 1 (844) 522-7314 to verify your account and
							have your account funding limit removed.
							{infoBannerType === 'error' &&
								' You can then add more funds to your account.'}
						</>
					}
					dataqa="warning-message"
				/>
			);
		}
	};

	render() {
		const { modalId } = this.props;
		const { customAmount, selectedOption, showInlineErrorMessage } = this.state;
		const { brandName } = Session.getBrand();
		const isEndicia = brandName === 'Endicia';
		const balance = Session.getBalance();
		const { controlTotal = 0 } = balance;
		const { lifetimeFundingLimit } = Session.getAccountInfo();
		let infoBannerType = null;

		if (lifetimeFundingLimit) {
			if (showInlineErrorMessage) {
				infoBannerType = 'error';
			} else if (controlTotal < lifetimeFundingLimit) {
				infoBannerType = 'information';
			} else {
				infoBannerType = 'warning';
			}
		}

		return (
			<Modal
				modalId={modalId}
				className="common-payment add-funds-modal"
				header="Add Funds"
				primaryButton={{
					text: 'Purchase',
					type: ModalButtonActionType.Ok,
					onClick: this.onPurchaseClick,
				}}
				size="small"
			>
				{infoBannerType && this.getInfoBanner(infoBannerType, lifetimeFundingLimit)}
				<div className="add-funds-radio-buttons">
					{/* Default add fund options */}
					{[10, 25, 50, 100].map(value => (
						<WrappedRadioButton
							key={`input-option-${value}`}
							name={value}
							value={value}
							onChange={this.onRadioOptionChange}
							checked={value === selectedOption}
							dataqa={`add-funds-${value}`}
						>
							{usCurrencyFormatter.format(value)}
						</WrappedRadioButton>
					))}
					<div className="add-other-amount">
						<WrappedRadioButton
							name="customAmount"
							onChange={this.onRadioOptionChange}
							checked={selectedOption === 'customAmount'}
							dataqa="add-funds-custom-amount"
						/>
						<WrappedInputNumberWithLabel
							label="Other: $ "
							uiElementConfig={{
								dataqa: 'purchase-other-amount',
								name: 'customAmount',
								value: customAmount,
								min: 0,
								mode: 'decimal',
								minFractionDigits: 2,
								maxFractionDigits: 2,
								incrementButtonIcon: 'pi pi-chevron-up',
								decrementButtonIcon: 'pi pi-chevron-down',
								showButtons: true,
								onChange: this.onCustomAmountChange,
								onFocus: this.onFocus,
							}}
						/>
					</div>
				</div>
				<WrappedButton
					label="Edit payment method"
					className="link"
					buttonType={WrappedButtonType.TertiaryGrey}
					onClick={this.onEditPaymentMethodLinkClick}
					dataqa="edit-payment-method"
				/>
				<WrappedButton
					label="Auto-fund account"
					className="link"
					buttonType={WrappedButtonType.TertiaryGrey}
					onClick={this.onAutoBuyPostageLinkClick}
					dataqa="auto-fund-account"
				/>
				<Link
					className="link free-account-credit"
					data-qa="earn-free-account-credit"
					dataqa="add-funds-earn-free-account-credit-link"
					onClick={this.onReferFriendLinkClick}
					navIcon={!isEndicia}
				>
					Earn Free Account Credit
				</Link>
			</Modal>
		);
	}
}

export default withLayoutAndModalContexts(AddFundsModal);
